/**
 * The audit-chain row hash (audit-trail-design.md §2.1; CC-11). THE forensic
 * spine — D10 for the whole product depends on this byte formula being exactly
 * §2.1. Any deviation is a chain-breaking bug.
 *
 *   row_hash[N] = SHA-256(
 *     prev_hash ⋮ event_id ⋮ sequence ⋮ event_type ⋮ actor_user_id ⋮
 *     actor_session_id ⋮ resource_type ⋮ resource_id ⋮ canonicalJson(payload) ⋮
 *     ip_address ⋮ user_agent ⋮ timestamp )
 *   prev_hash[0] = 64 zero hex chars
 *
 * where `⋮` is the U+001F unit-separator (prevents field-confusion attacks) and
 * every nullable field contributes the empty string when null. actor + network
 * fields ARE in the hash (F-083) so attributability is bound by the chain, not
 * merely stored alongside it.
 */
import { canonicalJson } from './canonical-json.ts';

/** The canonical inter-field separator (U+001F unit-separator) per §2.1. */
export const FIELD_SEPARATOR = '\u001f';

/** The genesis prev_hash: 64 zero hex chars (§2.1, prev_hash[0]). */
export const CHAIN_SEED = '0'.repeat(64);

/** A stored audit_events row (data-model §3 column set; timestamp in ms per §7). */
export interface AuditEventRow {
  event_id: string;
  sequence: number;
  event_type: string;
  actor_user_id: string | null;
  actor_session_id: string | null;
  case_id: string | null;
  resource_type: string | null;
  resource_id: string | null;
  /** Already-canonicalised deterministic JSON string. */
  payload_json: string;
  ip_address: string | null;
  user_agent: string | null;
  /** unix MILLISECONDS. */
  timestamp: number;
  prev_hash: string;
  row_hash: string;
  anchored_in: string | null;
}

/** The row as it exists before its own row_hash is computed. */
export type RowHashInput = Omit<AuditEventRow, 'row_hash'>;

/** Lower-case hex SHA-256 of a UTF-8 string, via Web Crypto (Workers + Node 22). */
export async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Compute row_hash for a pre-hash row per the exact §2.1 field order + separator.
 * The payload is re-canonicalised from `payload_json` (idempotent when the caller
 * already stored canonical JSON) so the hash is over the canonical form, never the
 * raw serialisation — matching the §2.1 reference `canonicalJson(JSON.parse(...))`.
 */
export async function computeRowHash(row: RowHashInput): Promise<string> {
  const parts = [
    row.prev_hash,
    row.event_id,
    String(row.sequence),
    row.event_type,
    row.actor_user_id ?? '',
    row.actor_session_id ?? '',
    row.resource_type ?? '',
    row.resource_id ?? '',
    canonicalJson(JSON.parse(row.payload_json)),
    row.ip_address ?? '',
    row.user_agent ?? '',
    String(row.timestamp),
  ];
  return sha256Hex(parts.join(FIELD_SEPARATOR));
}
